import { createAsyncThunk, createSlice, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "redux/store";
import { conversationApi } from "./conversationApi";
import { Conversation } from "data/types";

interface conversationState {
  loading: "idle" | "pending" | "succeeded" | "failed";
  error?: SerializedError;
  conversations: Conversation[] | undefined;
  conversation: Conversation[] | undefined;
  isUnread: boolean;
  unreadConversationsCount: number;
}

const initialState: conversationState = {
  loading: "idle",
  conversations: undefined,
  conversation: undefined,
  isUnread: false,
  unreadConversationsCount: 0,
};

export const createConversation = createAsyncThunk("conversation/createConversation", async (user2_id: number) => {
  return await conversationApi.createConversation(user2_id);
});

export const getChatByConversationId = createAsyncThunk(
  "conversation/getChatByConversationId",
  async (id: any) => {
    return await conversationApi.getChatByConversationId(id);
  }
);

export const getConversations = createAsyncThunk("conversation/getConversations", async (filter: string) => {
  return await conversationApi.getConversations(filter);
});

export const isUnreadMessages = createAsyncThunk("conversation/isUnreadMessages", async () => {
  return await conversationApi.isUnreadMessages();
});

export const conversationSlice = createSlice({
  name: "Conversation",
  initialState,
  reducers: {
    initStates(state) {
      return {
        ...state,
        loading: "idle",
      };
    },
  },
  extraReducers: (builder) => {
    // Handling createConversation actions
    builder.addCase(createConversation.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(createConversation.fulfilled, (state, action) => {
      state.loading = "succeeded";
    });
    builder.addCase(createConversation.rejected, (state) => {
      state.loading = "failed";
    });

    // Handling getConversations actions
    builder.addCase(getConversations.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(getConversations.fulfilled, (state, action) => {
      state.loading = "succeeded";
      state.conversations = action.payload.conversations;
      state.unreadConversationsCount = action.payload.unreadConversationsCount;
    });
    builder.addCase(getConversations.rejected, (state) => {
      state.loading = "failed";
    });

    // Handling isUnreadMessages actions
    builder.addCase(isUnreadMessages.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(isUnreadMessages.fulfilled, (state, action) => {
      state.loading = "succeeded";
      state.isUnread = action.payload;
    });
    builder.addCase(isUnreadMessages.rejected, (state) => {
      state.loading = "failed";
    });

    // Handling getChatByConversationId actions
    builder.addCase(getChatByConversationId.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(getChatByConversationId.fulfilled, (state, action) => {
      state.loading = "succeeded";
      state.conversation = action.payload;
    });
    builder.addCase(getChatByConversationId.rejected, (state) => {
      state.loading = "failed";
    });
  },
});

export const { initStates } = conversationSlice.actions;

export const selectCount = (state: RootState) => state.conversation;
export default conversationSlice.reducer;
